import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronDown, Search } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import type { User } from '@/types';

interface LogsUserFilterProps {
  selected: string[];
  onSelect: (selected: string[]) => void;
  title?: string;
}

export function LogsUserFilter({ selected, onSelect, title }: LogsUserFilterProps) {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { t, dir, language } = useLanguage();
  const isRTL = dir === 'rtl';
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const usersQuery = query(collection(db, 'users'));
        const snapshot = await getDocs(usersQuery);
        const usersData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as User[];
        setUsers(usersData);
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const handleSelect = useCallback((value: string, checked: boolean) => {
    if (checked) {
      onSelect([...selected, value]);
    } else {
      onSelect(selected.filter(item => item !== value));
    }
  }, [selected, onSelect]);

  const getUserDisplayName = useCallback((user: User) => {
    console.log('Language:', language, 'User:', user.firstNameEn, user.firstNameAr);
    if (language === 'en') {
      if (user.firstNameEn && user.lastNameEn) {
        return `${user.firstNameEn} ${user.lastNameEn}`;
      } else if (user.firstNameAr && user.lastNameAr) {
        return `${user.firstNameAr} ${user.lastNameAr}`;
      } else {
        return user.email || 'Unknown User';
      }
    } else {
      if (user.firstNameAr && user.lastNameAr) {
        return `${user.firstNameAr} ${user.lastNameAr}`;
      } else if (user.firstNameEn && user.lastNameEn) {
        return `${user.firstNameEn} ${user.lastNameEn}`;
      } else {
        return user.email || 'Unknown User';
      }
    }
  }, [language]);

  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      const displayName = getUserDisplayName(user).toLowerCase();
      const email = (user.email || '').toLowerCase();
      const phone = (user.phone || '').toLowerCase();
      const searchLower = searchTerm.toLowerCase();
      
      return displayName.includes(searchLower) || 
             email.includes(searchLower) || 
             phone.includes(searchLower);
    });
  }, [users, searchTerm, getUserDisplayName]);

  const displayText = useMemo(() => {
    const selectedCount = selected.length;
    if (selectedCount === 0) {
      return title || t('filters.usersTitle');
    } else if (selectedCount === 1) {
      const selectedUser = users.find(user => user.id === selected[0]);
      return selectedUser ? getUserDisplayName(selectedUser) : `${selectedCount} ${t('filters.selected')}`;
    } else {
      return `${selectedCount} ${t('filters.selected')}`;
    }
  }, [selected, users, title, t, getUserDisplayName]);

  return (
    <div className="relative" ref={dropdownRef}>
      <Button 
        variant="outline" 
        className={`h-9 w-full ${isRTL ? 'flex-row-reverse' : ''}`}
        disabled={loading}
        onClick={() => setOpen(!open)}
      >
        {loading ? t('common.loading') : displayText}
        <ChevronDown className={`h-4 w-4 ${isRTL ? 'mr-2' : 'ml-2'}`} />
      </Button>
      
      {open && (
        <div className={`absolute top-full mt-1 w-80 bg-popover border rounded-md shadow-lg z-50 ${isRTL ? 'right-0' : 'left-0'}`}>
          {/* Search field */}
          <div className="p-3 border-b">
            <div className="relative">
              <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground`} />
              <Input
                placeholder={t('common.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={isRTL ? 'pr-9' : 'pl-9'}
                dir={dir}
              />
            </div>
          </div>
          
          {/* Scrollable content area */}
          <div className="max-h-64 overflow-y-auto">
            {filteredUsers.map((user) => (
              <div
                key={user.id}
                className={`flex items-center px-3 py-2 hover:bg-accent cursor-pointer ${isRTL ? 'text-right' : 'text-left'}`}
                onClick={() => handleSelect(user.id, !selected.includes(user.id))}
                dir={dir}
              >
                <Checkbox
                  checked={selected.includes(user.id)}
                  className={`${isRTL ? 'ml-3' : 'mr-3'}`}
                  readOnly
                />
                <div className={`flex flex-col flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                  <span className="text-sm">{getUserDisplayName(user)}</span>
                  <span className="text-xs text-muted-foreground">{user.phone || user.email}</span>
                </div>
              </div>
            ))}
            {filteredUsers.length === 0 && searchTerm && (
              <div className={`p-2 text-sm text-muted-foreground text-center ${isRTL ? 'text-right' : 'text-left'}`} dir={dir}>
                {t('logs.noResults')}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
